import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store, Select } from '@ngxs/store';
import {
    Choice,
    ModalOverlayRef,
    ChoiceItem,
    DeepLinksActions,
    TrackingActions,
    ModalsService,
    ModulesSelectors,
    DefaultModalComponent,
} from '@serviceos-ng/core';
import { finalize, take } from 'rxjs/operators';
import { Observable } from 'rxjs';

interface CancelRequestPayload {
    cancel_reason_title: string;
    cancel_reason_id: number;
    comment: string;
}

@Component({
    selector: 'serviceos-web-cancel-booking-modal',
    templateUrl: './cancel-booking-modal.component.html',
    styleUrls: ['./cancel-booking-modal.component.scss'],
    // encapsulation: ViewEncapsulation.None,
})
export class CancelBookingModalComponent implements OnInit {

    @Select(ModulesSelectors.getModuleConfigText('bookings'))
    moduleBookingsTexts$!: Observable<any>;

    public texts: any = {};
    
    public cancelForm: { id: number; choices: Choice[] } = {
        id: 131, //random id
        choices: [],
    };

    public showFormLoader = false;
    public formErrorMessage = '';

    public bookingsTexts:any = {};

    public formCompleted = false;
    public successMessage = 'Success'; //! TODO: should be from response
    public confirmCancelBookingConfig: any = null;

    constructor(
        private modalRef: ModalOverlayRef,
        private store: Store,
        private translateService: TranslateService,
        private router: Router,
        private _modalsService: ModalsService,
        private _store: Store
    ) {

        // Set localized texts
        this.texts = this.store.selectSnapshot(ModulesSelectors.getModuleConfigText('bookings'));
    }

    ngOnInit(): void {
        
        // Set localized texts
        this.bookingsTexts = this.store.selectSnapshot(ModulesSelectors.getModuleConfigText('bookings'));
        
        this.confirmCancelBookingConfig = this._store.selectSnapshot(ModulesSelectors.getComponentFromModule('account', 'confirm_cancel_booking'));

        this.buildCancelForm();
    }

    private buildCancelForm(): void {
        if (this.modalRef.configData?.resources?.reasons?.length) {
            let cancelTerm: any = null;
            if (this.modalRef.configData?.resources?.terms) {
                cancelTerm = this.modalRef.configData?.resources.terms;
            }

            // build main choice
            // eslint-disable-next-line prefer-const
            let choice: Choice = {
                id: 1211,
                title: this.bookingsTexts?.cancel_form_title ? this.bookingsTexts.cancel_form_title : 'Can you please tell us the reason for your cancellation?',
                description: '',
                sort: 1,
                positions: ['none'],
                type: 'default',
                required: true,
                submit: 'on_submit',
                summary_title: '',
                customize: {
                    actions_button_styles: {
                        on_submit: 'primary',
                    },
                    terms: cancelTerm,
                },
                actions: [
                    {
                        endpoint: null,
                        type: 'on_submit',
                        active_to: this.modalRef.configData.active_to,
                        deep_link: 'action=submit_cancel_reason',
                        title: this.bookingsTexts?.cancel_booking_button ? this.bookingsTexts.cancel_booking_button : 'Confirm cancellation',
                    },
                ],
                choice_items: [],
            };

            this.modalRef.configData.resources.reasons.forEach(
                (reason: any) => {
                    const choiceItem: ChoiceItem = {
                        id: reason.id,
                        title: reason.name,
                        required: reason.requires_comment,
                        show_comment: reason.show_comment,
                        sort: reason.sort,
                        type: 'radio',
                        value: 0,
                        choice_items: [],
                    };

                    const childChoiceItem: ChoiceItem = {
                        id: Number(reason.id + 1),
                        title: '',
                        required: reason.requires_comment,
                        show_comment: reason.show_comment,
                        description: '',
                        type: 'text_field',
                        value: '',
                    };

                    choiceItem.choice_items.push(childChoiceItem);

                    if (choice.choice_items)
                        choice.choice_items.push(choiceItem);
                }
            );

            this.cancelForm.choices.push(choice);
        } else {
            // No leave_reasons form config

            let noReasonsCancelTerm: any = null;
            if (this.modalRef.configData?.resources?.terms) {
                noReasonsCancelTerm = this.modalRef.configData?.resources.terms;
            }

            const choice: Choice = {
                id: 1211,
                title: this.bookingsTexts?.success_cancel_booking_title ? this.bookingsTexts.success_cancel_booking_title : 'Please confirm your cancellation',
                description: '',
                sort: 1,
                positions: ['none'],
                type: 'default',
                required: true,
                submit: 'on_submit',
                summary_title: '',
                customize: {
                    actions_button_styles: {
                        on_submit: 'primary',
                    },
                    terms: noReasonsCancelTerm,
                },
                actions: [
                    {
                        endpoint: null,
                        type: 'on_submit',
                        active_to: this.modalRef.configData.active_to,
                        deep_link: 'action=submit_cancel_reason',
                        title: this.bookingsTexts?.cancel_booking_button ? this.bookingsTexts.cancel_booking_button : 'Confirm cancellation',
                    },
                ],
                choice_items: [],
            };

            this.cancelForm.choices.push(choice);
        }
    }

    /**
     * Action handler for form
     * @param action
     */
    public onFormAction(action: any): void {
        this.showFormLoader = true;
        this.formErrorMessage = '';

        const isDoubleConfirmationEnabled = !!this.confirmCancelBookingConfig;

        // Show confirmation popup before cancel booking
        if (isDoubleConfirmationEnabled) {
            const dialogRef = this._modalsService.open(
                DefaultModalComponent, {
                    componentData: {
                        title: this.confirmCancelBookingConfig.title,
                        message: this.confirmCancelBookingConfig.description,
                        buttons: [
                            {
                                title: this.confirmCancelBookingConfig.cancel_button_title,
                                type: 'close',
                            },
                            {
                                title: this.confirmCancelBookingConfig.confirm_button_title,
                                type: 'confirm',
                            }
                        ]
                    }
                }
            );
    
            dialogRef.close$.pipe(take(1)).subscribe((res) => {
                if (res === 'confirm') {    
                    this.submitCancelBooking()
                } else {
                    this.showFormLoader = false;
                }
            });
        } else {
            this.submitCancelBooking();
        }
    }

    private submitCancelBooking() {
        // NOTE: For now we have only 1 action for the form
        const payloadData: CancelRequestPayload = this.extractPayloadData();
        this.store.dispatch(
            new TrackingActions.Push('successfulSubmit', {
                radioChoice: payloadData.cancel_reason_title,
                additionalNotes: payloadData.comment,
            })
        );

        this.store
            .dispatch(
                new DeepLinksActions.SubmitDeepLinkResult$(
                    this.modalRef.configData,
                    {
                        cancel_reason_id: payloadData.cancel_reason_id,
                        comment: payloadData.comment,
                    }
                )
            )
            .pipe(
                finalize(() => {
                    this.showFormLoader = false;
                })
            )
            .subscribe(
                (res) => {
                    this.showFormLoader = false;
                    this.store.dispatch(
                        new TrackingActions.Push('bookingCancelled', {
                            canceledRefNum: this.modalRef.configData
                                .deep_link_object.fs_book_ref_id,
                        })
                    );
                    // this.modalRef.close();
                    this.formCompleted = true;
                },
                (error) => {
                    this.formErrorMessage = error?.error[0]?.message;
                }
            );
    }

    /**
     * Extract payload data for cancel request
     */
    private extractPayloadData(): CancelRequestPayload {
        // eslint-disable-next-line prefer-const
        let cancelRequestPayloadData: any = {};

        this.cancelForm.choices[0].choice_items?.map(
            (choiceItem: ChoiceItem) => {
                // Current radio button is selected
                if (choiceItem.value) {
                    cancelRequestPayloadData.cancel_reason_title =
                        choiceItem.title;
                    cancelRequestPayloadData.cancel_reason_id = choiceItem.id;

                    if (choiceItem.choice_items?.length) {
                        cancelRequestPayloadData.comment =
                            choiceItem.choice_items[0].value;
                    }
                }
            }
        );

        return cancelRequestPayloadData;
    }

    /**
     * Handler for clicking success page confirm button
     */
    public onSuccessScreenConfirmation(): void {
        this.modalRef.close();
    }
}
